// Copy for the Print Center (/admin/print-center). Albanian is the primary
// language for shop staff; English mirrors it. Merged into the global
// translations under the `printCenter` namespace.
//
// Wording rules: "on hand" is the physical stock in the store; "needs labels"
// is received stock still waiting for a Black & White label; "extra" is any
// label that does not settle received stock (manual, reprint, replacement).
// Never claim labels "printed" unless a person confirmed it.

const tx = (sq, en) => ({ sq, en });

const printCenterTranslations = {
  // ── Page ─────────────────────────────────────────────────
  eyebrow: tx("Operacionet", "Operations"),
  pageTitle: tx("Qendra e printimit", "Print Center"),
  pageSubtitle: tx(
    "Gjej produktet, zgjidh variantet dhe printo etiketat me barkod. Mallrat e pranuara që presin etiketë shfaqen këtu automatikisht.",
    "Find products, choose variants and print barcode labels. Received stock that is waiting for labels appears here automatically."
  ),
  tab_needs_labels: tx("Pa etiketa", "Needs labels"),
  tab_catalog: tx("Katalogu", "Catalog"),
  tab_history: tx("Historiku", "History"),
  loading: tx("Duke u ngarkuar…", "Loading…"),
  loadError: tx("Të dhënat nuk u ngarkuan.", "Could not load the data."),
  retry: tx("Provo përsëri", "Try again"),
  actionError: tx("Veprimi nuk u krye. Provo përsëri.", "That did not work. Please try again."),
  close: tx("Mbyll", "Close"),
  cancel: tx("Anulo", "Cancel"),
  back: tx("Kthehu", "Back"),
  next: tx("Tjetra", "Next"),
  previous: tx("E mëparshmja", "Previous"),
  pageOf: tx("Faqja {page} nga {total}", "Page {page} of {total}"),
  decrease: tx("Zvogëlo", "Decrease"),
  increase: tx("Rrit", "Increase"),
  variantFallback: tx("Varianti", "Variant"),
  unitTag: tx("Copë", "Unit"),
  serializedPill: tx("Me copa", "Serialized"),
  lastPrinted: tx("Puna #{job} u dërgua në printim. Zgjedhja u pastrua.", "Job #{job} was sent to print. The selection was cleared."),
  viewJob: tx("Shiko punën", "View job"),
  restoreSelection: tx("Rikthe zgjedhjen", "Restore selection"),
  unavailableAfterRefreshOne: tx("{count} artikull i zgjedhur nuk ekziston më ose nuk është në stok.", "{count} selected item no longer exists or is no longer in stock."),
  unavailableAfterRefreshOther: tx("{count} artikuj të zgjedhur nuk ekzistojnë më ose nuk janë në stok.", "{count} selected items no longer exist or are no longer in stock."),

  // ── Counts ───────────────────────────────────────────────
  labelCountOne: tx("{count} etiketë", "{count} label"),
  labelCountOther: tx("{count} etiketa", "{count} labels"),
  sheetCountOne: tx("{count} fletë", "{count} sheet"),
  sheetCountOther: tx("{count} fletë", "{count} sheets"),
  lineCountOne: tx("{count} variant", "{count} variant"),
  lineCountOther: tx("{count} variante", "{count} variants"),
  productCountOne: tx("{count} produkt", "{count} product"),
  productCountOther: tx("{count} produkte", "{count} products"),
  itemCountOne: tx("{count} variant", "{count} variant"),
  itemCountOther: tx("{count} variante", "{count} variants"),
  pageEstimateOne: tx("~{count} fletë", "~{count} sheet"),
  pageEstimateOther: tx("~{count} fletë", "~{count} sheets"),
  needsLabelsCountOne: tx("{count} pa etiketë", "{count} needs a label"),
  needsLabelsCountOther: tx("{count} pa etiketa", "{count} need labels"),
  blockedCountOne: tx("{count} nuk printohet", "{count} can't print"),
  blockedCountOther: tx("{count} nuk printohen", "{count} can't print"),
  attemptCountOne: tx("{count} printim", "{count} attempt"),
  attemptCountOther: tx("{count} printime", "{count} attempts"),
  onHandTotal: tx("{count} në dyqan", "{count} on hand"),
  onHandShort: tx("Në dyqan", "On hand"),
  onHandHint: tx("Copat fizike në dyqan, përfshirë të rezervuarat.", "Physical units in the store, including reserved ones."),
  needsShort: tx("Pa etiketë", "Needs"),
  needsLabelsHint: tx("Mallra të pranuara që ende presin etiketë.", "Received units still waiting for a label."),
  pendingJobHint: tx("{quantity} në punën #{job} nga {name} (pret konfirmim)", "{quantity} in job #{job} by {name} (awaiting confirmation)"),

  // ── Catalog ──────────────────────────────────────────────
  filtersTitle: tx("Filtrat", "Filters"),
  resultsTitle: tx("Produktet", "Products"),
  searchPlaceholder: tx("Kërko emër, kod, SKU ose barkod — ose skano", "Search name, code, SKU or barcode — or scan"),
  searchScanHint: tx("Skano + Enter shton etiketën", "Scan + Enter adds the label"),
  filterNeedsLabels: tx("Pa etiketa", "Needs labels"),
  filterMissingBarcode: tx("Pa barkod", "Missing barcode"),
  filterType: tx("Lloji", "Type"),
  filterType_shoes: tx("Këpucë", "Footwear"),
  filterType_textile: tx("Tekstil", "Textile"),
  filterType_accessories: tx("Aksesorë", "Accessories"),
  filterBrand: tx("Marka", "Brand"),
  filterCategory: tx("Kategoria", "Category"),
  filterColor: tx("Ngjyra", "Colour"),
  filterSize: tx("Madhësia", "Size"),
  filterReceived: tx("Pranuar", "Received"),
  filterCreated: tx("Krijuar", "Created"),
  filterStatus: tx("Statusi", "Status"),
  filterStatusLive: tx("Aktive dhe drafte", "Active and drafts"),
  filterStock: tx("Stoku", "Stock"),
  filterLocation: tx("Lokacioni", "Location"),
  filterAllLocations: tx("Të gjitha lokacionet", "All locations"),
  filterAny: tx("Të gjitha", "Any"),
  filterReceived_1: tx("Sot", "Today"),
  filterReceived_7: tx("7 ditët e fundit", "Last 7 days"),
  filterReceived_30: tx("30 ditët e fundit", "Last 30 days"),
  filterCreated_7: tx("Krijuar 7 ditët e fundit", "Created in the last 7 days"),
  filterCreated_30: tx("Krijuar 30 ditët e fundit", "Created in the last 30 days"),
  filterStock_in_stock: tx("Në stok", "In stock"),
  filterStock_out_of_stock: tx("Pa stok", "Out of stock"),
  status_active: tx("Aktiv", "Active"),
  status_draft: tx("Draft", "Draft"),
  status_archived: tx("Arkivuar", "Archived"),
  sortLabel: tx("Rendit", "Sort"),
  sort_recently_received: tx("Pranuar së fundi", "Recently received"),
  sort_labels_needed: tx("Më shumë pa etiketa", "Most labels needed"),
  sort_newest: tx("Më të rejat", "Newest"),
  sort_name: tx("Emri A–Z", "Name A–Z"),
  sort_stock: tx("Më shumë stok", "Most stock"),
  chipSearch: tx("“{value}”", "“{value}”"),
  chipSize: tx("Madhësia {value}", "Size {value}"),
  removeFilter: tx("Hiq filtrin {name}", "Remove filter {name}"),
  clearFilters: tx("Pastro filtrat", "Clear filters"),
  selectShownOne: tx("Zgjidh {count} të shfaqur", "Select {count} shown"),
  selectShownOther: tx("Zgjidh {count} të shfaqurit", "Select {count} shown"),
  selectAllMatchingOne: tx("Zgjidh të gjitha nga {count} produkt që përputhet", "Select everything from {count} matching product"),
  selectAllMatchingOther: tx("Zgjidh të gjitha nga {count} produkte që përputhen", "Select everything from {count} matching products"),
  selectAllAddedOne: tx("U shtua {count} variant.", "Added {count} variant."),
  selectAllAddedOther: tx("U shtuan {count} variante.", "Added {count} variants."),
  selectAllTruncated: tx(
    "U shtuan {count} variantet e para nga {total}. Ngushto filtrat për të tjerat.",
    "Added the first {count} of {total} variants. Narrow the filters for the rest."
  ),
  noResults: tx("Nuk ka produkte.", "No products."),
  noResultsFiltered: tx("Asnjë produkt nuk përputhet me këta filtra.", "No products match these filters."),
  loadMore: tx("Shfaq më shumë", "Load more"),
  selectProduct: tx("Zgjidh {name}", "Select {name}"),
  selectColor: tx("Zgjidh ngjyrën {color}", "Select colour {color}"),
  selectSize: tx("Zgjidh madhësinë {size}", "Select size {size}"),
  selectItem: tx("Zgjidh {name}", "Select {name}"),
  selectedOf: tx("{selected} nga {total} zgjedhur", "{selected} of {total} selected"),
  matrixColorSize: tx("Ngjyra / madhësia", "Colour / size"),
  matrixNoVariant: tx("Nuk ka variant", "No variant"),
  noVariants: tx("Ky produkt nuk ka variante aktive.", "This product has no live variants."),
  noUnitsInStock: tx("Nuk ka copa në stok për t'u etiketuar.", "No units in stock to label."),
  printQtyTag: tx("Printo {count}", "Print {count}"),
  openProduct: tx("Hap produktin", "Open product"),
  blockedMissingBarcode: tx("Pa barkod — shtoje te produkti.", "No barcode — add one on the product."),
  blockedSerializedVariant: tx(
    "Produkt me copa: printohet etiketa e çdo cope, jo barkodi i variantit.",
    "Serialized product: each unit's own label is printed, never the variant barcode."
  ),
  scanAdded: tx("U shtua: {name}", "Added: {name}"),
  scanSerializedVariant: tx(
    "Ky është barkodi i variantit të një produkti me copa. Skano etiketën e copës ose zgjidh copat te katalogu.",
    "That is the variant barcode of a serialized product. Scan the unit's own label or pick the units in the catalog."
  ),
  scanUnitNotInStock: tx("Kjo copë nuk është më në stok.", "This unit is no longer in stock."),

  // ── Needs labels ─────────────────────────────────────────
  needsHeroEyebrow: tx("Mallra të pranuara", "Received stock"),
  labelsNeededOne: tx("{count} etiketë duhet printuar", "{count} label needed"),
  labelsNeededOther: tx("{count} etiketa duhen printuar", "{count} labels needed"),
  needsHeroMeta: tx("{items} variante · {products} produkte", "{items} variants · {products} products"),
  pendingElsewhereOne: tx("{count} në një printim që pret konfirmim", "{count} in a print awaiting confirmation"),
  pendingElsewhereOther: tx("{count} në printime që presin konfirmim", "{count} in prints awaiting confirmation"),
  selectAllNeededOne: tx("Zgjidh {count} etiketën", "Select the {count} label"),
  selectAllNeededOther: tx("Zgjidh të {count} etiketat", "Select all {count} labels"),
  selectDay: tx("Zgjidh {day}", "Select {day}"),
  needsOfTotal: tx("{count} nga kjo ditë ({total} gjithsej)", "{count} from this day ({total} in total)"),
  receivedVia_restock: tx("Rimbushur {time} · {name}", "Restocked {time} · {name}"),
  receivedVia_scan_receive: tx("Skanuar {time} · {name}", "Scanned in {time} · {name}"),
  receivedVia_initial_stock: tx("Stok fillestar {time} · {name}", "Initial stock {time} · {name}"),
  needsEmptyTitle: tx("Të gjitha mallrat e pranuara kanë etiketa", "All received stock has labels"),
  needsEmptyCopy: tx(
    "Kur pranohen mallra që duan etiketë, ato shfaqen këtu. Stoku i vjetër mund të printohet nga katalogu.",
    "When stock that needs labels is received, it appears here. Older stock can be printed from the catalog."
  ),
  browseCatalog: tx("Hap katalogun", "Browse the catalog"),
  awaitingCountOne: tx("{count} printim pret konfirmim", "{count} print is awaiting confirmation"),
  awaitingCountOther: tx("{count} printime presin konfirmim", "{count} prints are awaiting confirmation"),
  awaitingCopy: tx(
    "Etiketat e tyre nuk llogariten si të printuara derisa dikush të konfirmojë që dolën mirë.",
    "Their labels don't count as printed until someone confirms they came out right."
  ),
  dismiss: tx("Nuk duhen", "Not needed"),
  dismissTitle: tx("Këto etiketa nuk duhen?", "These labels aren't needed?"),
  dismissCopy: tx(
    "Mallrat mbeten në stok; vetëm hiqen nga lista e etiketave që duhen printuar. Arsyeja ruhet.",
    "The stock stays; it is only removed from the labels to print. The reason is recorded."
  ),
  dismissQuantity: tx("Sasia", "Quantity"),
  dismissMax: tx("nga {count}", "of {count}"),
  dismissReason: tx("Arsyeja", "Reason"),
  dismissReasonLabelled: tx("Erdhën me etiketë", "Arrived already labelled"),
  dismissReasonSupplier: tx("Përdoret barkodi i furnizuesit", "Supplier barcode is used"),
  dismissReasonMiscount: tx("Gabim në pranim", "Received by mistake"),
  dismissReasonOther: tx("Tjetër", "Other"),
  dismissReasonPlaceholder: tx("Shkruaj arsyen", "Write the reason"),
  dismissConfirm: tx("Shëno si të panevojshme", "Mark as not needed"),
  dismissDoneOne: tx("{count} etiketë u shënua si e panevojshme.", "{count} label marked as not needed."),
  dismissDoneOther: tx("{count} etiketa u shënuan si të panevojshme.", "{count} labels marked as not needed."),
  dismissChanged: tx("Tani mund të hiqen vetëm {count}. Rifresko dhe provo përsëri.", "Only {count} can be dismissed now. Refresh and try again."),

  // ── Tray ─────────────────────────────────────────────────
  trayTitle: tx("Për printim", "Print tray"),
  trayActions: tx("Veprime me sasitë", "Quantity actions"),
  trayEmptyTitle: tx("Asgjë e zgjedhur ende", "Nothing selected yet"),
  trayEmptyCopy: tx(
    "Zgjidh produkte, ngjyra ose madhësi. Zgjedhja mbetet edhe kur kërkon apo ndryshon filtrat.",
    "Select products, colours or sizes. The selection stays while you search or change filters."
  ),
  traySplitNew: tx("{count} për mallra të reja", "{count} for new stock"),
  traySplitExtra: tx("{count} shtesë", "{count} extra"),
  trayOnHand: tx("Në dyqan: {count}", "On hand: {count}"),
  trayNeedsLabels: tx("Pa etiketë: {count}", "Needs labels: {count}"),
  qtyNewStock: tx("Mallra të reja", "New stock"),
  qtyExtra: tx("Shtesë / riprintim", "Extra / reprint"),
  qtyNewStockFor: tx("Etiketa për mallra të reja: {name}", "New-stock labels for {name}"),
  qtyExtraFor: tx("Etiketa shtesë: {name}", "Extra labels for {name}"),
  qtyPrint: tx("Printo {count}", "Print {count}"),
  qtyZero: tx("0 — nuk printohet", "0 — not printed"),
  removeLine: tx("Hiq {name}", "Remove {name}"),
  issueNewStockTooHigh: tx("Vetëm {available} presin etiketë; pjesa tjetër duhet të jetë shtesë.", "Only {available} still need labels; the rest must be extra."),
  issueAboveOnHand: tx("{quantity} etiketa, por vetëm {onHand} në dyqan.", "{quantity} labels, but only {onHand} on hand."),
  issueUnavailable: tx("Nuk ekziston më ose nuk është në stok — hiqe.", "No longer exists or in stock — remove it."),
  bulkAllOne: tx("Nga 1 për secilin", "Set all to 1"),
  bulkOnHand: tx("Sipas stokut në dyqan", "Set to stock on hand"),
  bulkOutstanding: tx("Sipas mallrave pa etiketë", "Set to labels still needed"),
  bulkRemoveZero: tx("Hiq rreshtat me 0", "Remove lines with 0"),
  bulkClear: tx("Pastro të gjitha", "Clear everything"),
  clearConfirmTitle: tx("Të pastrohet zgjedhja?", "Clear the selection?"),
  clearConfirmCopyOne: tx("{count} etiketë do të hiqet nga lista.", "{count} label will be removed from the tray."),
  clearConfirmCopyOther: tx("{count} etiketa do të hiqen nga lista.", "{count} labels will be removed from the tray."),
  clearConfirm: tx("Pastro", "Clear"),
  fixBeforePrintingOne: tx("Rregullo {count} artikull para printimit.", "Fix {count} item before printing."),
  fixBeforePrintingOther: tx("Rregullo {count} artikuj para printimit.", "Fix {count} items before printing."),
  zeroLinesNoteOne: tx("{count} rresht me 0 etiketa nuk printohet.", "{count} line with 0 labels is not printed."),
  zeroLinesNoteOther: tx("{count} rreshta me 0 etiketa nuk printohen.", "{count} lines with 0 labels are not printed."),
  reviewAndPrint: tx("Shiko dhe printo · {count}", "Review & print · {count}"),

  // ── Review & print ───────────────────────────────────────
  reviewEyebrow: tx("Para printimit", "Before printing"),
  reviewAgainEyebrow: tx("Printo sërish punën #{job}", "Print job #{job} again"),
  reviewTitleOne: tx("{count} etiketë", "{count} label"),
  reviewTitleOther: tx("{count} etiketa", "{count} labels"),
  reviewSummary: tx("Përmbledhja", "Summary"),
  sumProducts: tx("Produkte", "Products"),
  sumLines: tx("Variante", "Variants"),
  sumLabels: tx("Etiketa", "Labels"),
  sumPages: tx("Fletë", "Sheets"),
  reviewNewStockNote: tx(
    "Etiketat për mallra të reja llogariten si të printuara vetëm pasi të konfirmosh që dolën mirë.",
    "New-stock labels only count as printed after you confirm they came out right."
  ),
  presetLabel: tx("Fleta e etiketave", "Label sheet"),
  templateLabel: tx("Dizajni i etiketës", "Label design"),
  "template_footwear-classic": tx("Këpucë klasike", "Footwear Classic"),
  "template_footwear-classic_hint": tx("Emri, ngjyra, numri, çmimi", "Name, colour, size, price"),
  "template_textile-modern": tx("Tekstil modern", "Textile Modern"),
  "template_textile-modern_hint": tx("Kodi, madhësia, ngjyra, çmimi", "Code, size, colour, price"),
  qrUnavailableTitle: tx("Etiketat do të printohen pa QR", "Labels will print without a QR code"),
  qrFailedCopy: tx(
    "Lidhjet QR nuk u ngarkuan. Barkodi dhe të dhënat e tjera printohen normalisht. Hape përsëri dialogun për të provuar sërish.",
    "The QR links could not be loaded. The barcode and everything else print normally. Reopen this dialog to try again."
  ),
  qrDisabledCopy: tx(
    "Adresa e dyqanit nuk është konfiguruar në server, ndaj QR nuk shfaqet. Barkodi printohet normalisht.",
    "The storefront address is not configured on the server, so no QR is shown. The barcode prints normally."
  ),
  presetShort: tx("{columns} × {rows}", "{columns} × {rows}"),
  presetMeta: tx(
    "A4 · {columns} × {rows} = {slots} etiketa · {width} × {height} mm secila",
    "A4 · {columns} × {rows} = {slots} labels · {width} × {height} mm each"
  ),
  offsetsTitle: tx("Rregullimi i printerit (vetëm ky kompjuter)", "Printer alignment (this computer only)"),
  offsetX: tx("X", "X"),
  offsetY: tx("Y", "Y"),
  offsetDecrease: tx("Zvogëlo {axis}", "Decrease {axis}"),
  offsetIncrease: tx("Rrit {axis}", "Increase {axis}"),
  offsetsNote: tx(
    "Lëviz gjithë fletën nëse ky printer printon pak anash. Ruhet vetëm në këtë shfletues.",
    "Shifts the whole sheet if this printer prints slightly off. Saved in this browser only."
  ),
  alignmentTest: tx("Printo fletë prove", "Print alignment test"),
  presetSettings: tx("Cilësimet e fletës", "Sheet settings"),
  presetDetails: tx("Detajet e fletës", "Sheet details"),
  startTitle: tx("Ku fillon printimi", "Where printing starts"),
  startCopy: tx(
    "Zgjidh etiketën e parë të lirë në fletën që vendos në printer. Faqet në vijim fillojnë gjithmonë nga 1.",
    "Pick the first free sticker on the sheet in the printer. Following pages always start at 1."
  ),
  startAt: tx("Fillo te etiketa", "Start at sticker"),
  startOf: tx("nga {count}", "of {count}"),
  startPrevious: tx("Etiketa e mëparshme", "Previous sticker"),
  startNext: tx("Etiketa tjetër", "Next sticker"),
  startRows: tx("Fillo nga rreshti", "Start from row"),
  startRow: tx("Rreshti {row}", "Row {row}"),
  startEmpty: tx("Shto sasi për të parë fletën.", "Add quantities to see the sheet."),
  startRange: tx("Etiketat {from}–{to} në këtë fletë", "Stickers {from}–{to} on this sheet"),
  startOverflowOne: tx(
    "{first} etiketa në këtë fletë · {rest} vazhdojnë në faqen 2",
    "{first} labels on this sheet · {rest} continue on page 2"
  ),
  startOverflowMany: tx(
    "{first} etiketa në këtë fletë · {rest} vazhdojnë në {pages} faqe të tjera",
    "{first} labels on this sheet · {rest} continue on {pages} more pages"
  ),
  startNextFree: tx(
    "Pas printimit, etiketa e parë e lirë: #{position} (faqja {page})",
    "After printing, the next free sticker is #{position} (page {page})"
  ),
  startSheetUsedUp: tx("Pas printimit, fleta e fundit përdoret e tëra.", "After printing, the last sheet is fully used."),
  suggestion: tx("Printimi i fundit nga ky shfletues mbaroi te #{last}.", "The last print from this browser ended at #{last}."),
  suggestionNote: tx("Vetëm sugjerim — kontrollo fletën në printer.", "Only a suggestion — check the sheet in the printer."),
  suggestionAction: tx("Fillo te #{position}", "Start at #{position}"),
  geometryChangedWarning: tx(
    "Printimi i fundit nga ky shfletues përdori një fletë tjetër. Kontrollo pozicionet para se të përdorësh një fletë të përdorur pjesërisht.",
    "The last print from this browser used a different sheet layout. Check positions before reusing a partially used sheet."
  ),
  previewHint: tx("Kështu do të printohet. Kliko një etiketë në faqen 1 për të filluar aty.", "This is exactly what prints. Click a sticker on page 1 to start there."),
  previewFrameTitle: tx("Parapamja e fletës së etiketave", "Label sheet preview"),
  previewMorePagesOne: tx("+{count} faqe tjetër nuk shfaqet në parapamje", "+{count} more page not shown in the preview"),
  previewMorePagesOther: tx("+{count} faqe të tjera nuk shfaqen në parapamje", "+{count} more pages not shown in the preview"),
  pageCaption: tx("Faqja {page}", "Page {page}"),
  pageCaptionRange: tx("etiketat {from}–{to}", "stickers {from}–{to}"),
  renderBlockedTitle: tx("Printimi është bllokuar", "Printing is blocked"),
  renderBlockedReason: tx("barkodi “{barcode}” nuk mund të printohet si CODE128", "barcode “{barcode}” cannot be encoded as CODE128"),
  densityTitle: tx("Disa barkode mund të mos lexohen", "Some barcodes may not scan"),
  densityUnsafeTitle: tx("Barkode shumë të ngjeshura për këtë etiketë", "Barcodes too dense for this label"),
  densityUnsafeCopy: tx(
    "Vijat dalin më të holla se 0,13 mm dhe shumë skanerë nuk i lexojnë. Përdor një etiketë më të gjerë ose printo një provë dhe skanoje para se të vazhdosh.",
    "The bars print thinner than 0.13 mm and many scanners cannot read them. Use a wider label, or print one test label and scan it before continuing."
  ),
  densityOverride: tx("E printova një provë dhe skanohet — printo gjithsesi", "I printed a test label and it scans — print anyway"),
  densityLegend: tx(
    "Gjerësia e vijës: ≥ 0,19 mm në rregull · 0,13–0,19 mm provo një · < 0,13 mm e pasigurt",
    "Bar width: ≥ 0.19 mm good · 0.13–0.19 mm test one · < 0.13 mm unsafe"
  ),
  density_healthy: tx("Barkodet: në rregull", "Barcodes: good"),
  density_warning: tx("Barkodet: provo një", "Barcodes: test one"),
  density_unsafe: tx("Barkodet: të pasigurta", "Barcodes: unsafe"),
  densityReason: tx("“{barcode}” printohet me vija {width} mm — provo ta skanosh para se të printosh shumë", "“{barcode}” prints with {width} mm bars — test-scan one before printing many"),
  largeJobWarningOne: tx("{count} etiketë.", "{count} label."),
  largeJobWarningOther: tx(
    "{count} etiketa — kontrollo që ke fletë të mjaftueshme në printer.",
    "{count} labels — make sure the printer has enough sheets."
  ),
  printerChecklist: tx(
    "Në dialogun e printimit: shkalla 100%, margjinat “Asnjë”, pa “Përshtat në faqe”.",
    "In the print dialog: scale 100%, margins “None”, no “Fit to page”."
  ),
  printLabelsOne: tx("Printo {count} etiketë", "Print {count} label"),
  printLabelsOther: tx("Printo {count} etiketa", "Print {count} labels"),
  printing: tx("Duke dërguar…", "Sending…"),
  sentToPrint: tx("Puna #{job} u dërgua në printim.", "Job #{job} was sent to print."),
  sentAwaiting: tx("Puna #{job} u dërgua në printim. Konfirmo kur të dalin etiketat.", "Job #{job} was sent to print. Confirm once the labels are out."),
  conflictTitle: tx("Disa etiketa po printohen nga dikush tjetër", "Some labels are already being printed by someone else"),
  conflictLine: tx("kërkuar {requested}, të lira {available}", "requested {requested}, free {available}"),
  conflictHeldBy: tx("{count} në punën #{job} nga {name}", "{count} in job #{job} by {name}"),
  conflictUseAvailable: tx("Përdor vetëm të lirat", "Use only the free ones"),
  conflictAsExtras: tx("Printoji si shtesë", "Print them as extras"),
  blockedServerTitle: tx("Këta artikuj nuk mund të printohen", "These items cannot be printed"),
  unavailableServerTitle: tx("Këta artikuj nuk ekzistojnë më ose nuk janë në stok", "These items no longer exist or are not in stock"),
  removeTheseItems: tx("Hiqi nga lista", "Remove them"),
  jobClosedError: tx("Kjo punë është mbyllur. Përdor “Riprinto” për ta printuar sërish.", "This job is closed. Use “Reprint” to print it again."),
  printAgainUnavailable: tx("Asnjë nga këto etiketa nuk mund të printohet më.", "None of these labels can be printed any more."),

  // ── Confirmation ─────────────────────────────────────────
  confirmQuestion: tx("A dolën mirë të gjitha etiketat?", "Did all labels print correctly?"),
  confirmJobMeta: tx("Puna #{job} · {total} etiketa · {newStock} për mallra të reja", "Job #{job} · {total} labels · {newStock} for new stock"),
  confirmAll: tx("Po, të gjitha", "Yes, all printed"),
  confirmSomeFailed: tx("Disa dështuan", "Some failed"),
  confirmNothing: tx("Asgjë nuk doli", "Nothing printed"),
  confirmLater: tx("Konfirmo më vonë", "Confirm later"),
  confirmLaterNote: tx("Puna pret konfirmim te “Pa etiketa” dhe “Historiku”.", "The job waits for confirmation under “Needs labels” and “History”."),
  confirmedAllOne: tx("{count} etiketë u konfirmua si e printuar.", "{count} label confirmed as printed."),
  confirmedAllOther: tx("{count} etiketa u konfirmuan si të printuara.", "{count} labels confirmed as printed."),
  confirmedPartial: tx("U konfirmuan etiketat që dolën mirë; të tjerat mbeten pa etiketë.", "The labels that printed were confirmed; the rest still need labels."),
  discarded: tx("Asgjë nuk u llogarit si e printuar.", "Nothing was counted as printed."),
  someFailedTitle: tx("Cilat etiketa dolën mirë?", "Which labels came out right?"),
  someFailedCopy: tx(
    "Për çdo rresht, shëno sa etiketa janë të përdorshme. Mund t'i printosh sërish ato që dështuan, ose të konfirmosh vetëm ato që dolën.",
    "For each line, set how many labels are usable. Print the failed ones again, or confirm only what printed."
  ),
  printedOk: tx("Dolën mirë", "Printed OK"),
  ofTotal: tx("nga {total}", "of {total}"),
  printFailedAgainOne: tx("Printo sërish {count} që dështoi", "Print the {count} failed one again"),
  printFailedAgainOther: tx("Printo sërish {count} që dështuan", "Print the {count} failed ones again"),
  confirmWhatPrinted: tx("Konfirmo vetëm këto", "Confirm only these"),
  lineNewStockOf: tx("{newStock} nga {total} për mallra të reja", "{newStock} of {total} for new stock"),

  // ── History ──────────────────────────────────────────────
  historyStatus: tx("Statusi", "Status"),
  historySource: tx("Burimi", "Source"),
  historyEmptyTitle: tx("Ende pa printime", "No prints yet"),
  historyEmptyCopy: tx("Çdo printim nga Qendra e printimit ruhet këtu.", "Every print from the Print Center is kept here."),
  jobTitle: tx("Puna #{job}", "Job #{job}"),
  reprintOf: tx("riprintim i #{job}", "reprint of #{job}"),
  jobStatus_generated: tx("Dërguar në printim", "Sent to print"),
  jobStatus_awaiting_confirmation: tx("Pret konfirmim", "Awaiting confirmation"),
  jobStatus_confirmed: tx("Konfirmuar", "Confirmed"),
  jobStatus_discarded: tx("Asgjë nuk doli", "Nothing printed"),
  jobStatusHint_generated: tx(
    "U dërgua në dialogun e printimit. Shfletuesi nuk e di nëse printeri e printoi.",
    "Sent to the print dialog. The browser cannot tell whether the printer printed it."
  ),
  jobStatusHint_awaiting_confirmation: tx(
    "Etiketat për mallra të reja janë të rezervuara derisa dikush të konfirmojë.",
    "New-stock labels are reserved until someone confirms."
  ),
  jobStatusHint_confirmed: tx("Dikush konfirmoi se etiketat dolën mirë.", "Someone confirmed the labels came out right."),
  jobStatusHint_discarded: tx("U shënua që asgjë nuk doli; etiketat u liruan.", "Marked as nothing printed; the labels were released."),
  jobSource_needs_labels: tx("Mallra të reja", "New stock"),
  jobSource_catalog: tx("Katalogu", "Catalog"),
  jobSource_scan: tx("Skanim", "Scan"),
  jobSource_reprint: tx("Riprintim", "Reprint"),
  confirmedBy: tx("Konfirmuar nga {name} · {time}", "Confirmed by {name} · {time}"),
  discardedBy: tx("Shënuar nga {name} · {time}", "Marked by {name} · {time}"),
  changedSinceOne: tx(
    "{count} etiketë ka ndryshuar që nga ky printim. Riprintimi përdor të dhënat aktuale.",
    "{count} label changed since this print. Reprints use the current data."
  ),
  changedSinceOther: tx(
    "{count} etiketa kanë ndryshuar që nga ky printim. Riprintimi përdor të dhënat aktuale.",
    "{count} labels changed since this print. Reprints use the current data."
  ),
  jobLines: tx("Etiketat", "Labels"),
  changeUnavailable: tx("Nuk ekziston më", "No longer available"),
  changeBarcode: tx("Barkodi ndryshoi", "Barcode changed"),
  changeBarcodeTitle: tx("Nga {from} në {to}", "From {from} to {to}"),
  changePrice: tx("Çmimi ndryshoi", "Price changed"),
  changeProduct: tx("Produkti ndryshoi", "Product changed"),
  lineConfirmed: tx("{count} konfirmuar", "{count} confirmed"),
  attemptsTitle: tx("Printimet", "Print attempts"),
  noAttempts: tx("Ende nuk është dërguar në printim.", "Not sent to print yet."),
  attemptLine: tx(
    "#{number} · {time} · {name} · nga etiketa {start}/{slots} · {labels} · {pages}",
    "#{number} · {time} · {name} · from sticker {start}/{slots} · {labels} · {pages}"
  ),
  attemptPartial: tx("vetëm disa rreshta", "some lines only"),
  confirmThisJob: tx("Konfirmo printimin", "Confirm this print"),
  printAgain: tx("Printo sërish", "Print again"),
  reprintAll: tx("Riprinto të gjitha", "Reprint all"),
  reprintSelectedOne: tx("Riprinto {count} të zgjedhur", "Reprint {count} selected"),
  reprintSelectedOther: tx("Riprinto {count} të zgjedhurit", "Reprint {count} selected"),
  duplicateSelection: tx("Kopjo si zgjedhje të re", "Duplicate as new selection"),
  reprintAddedOne: tx("{count} etiketë u shtua si riprintim.", "{count} label added as a reprint."),
  reprintAddedOther: tx("{count} etiketa u shtuan si riprintim.", "{count} labels added as reprints."),
  duplicateAddedOne: tx("{count} variant u shtua në listë.", "{count} variant added to the tray."),
  duplicateAddedOther: tx("{count} variante u shtuan në listë.", "{count} variants added to the tray."),

  // ── Presets ──────────────────────────────────────────────
  presetDrawerCopy: tx(
    "Fleta e etiketave e kompanisë: vlen për të gjithë. Rregullimi i printerit mbetet për çdo kompjuter.",
    "The company label sheet: it applies to everyone. Printer alignment stays per computer."
  ),
  presetReadOnly: tx("Vetëm administratori ose pronari mund ta ndryshojë fletën e kompanisë.", "Only an admin or owner can change the company label sheet."),
  presetSpacing: tx("Hapësirat", "Spacing"),
  spacing_density: tx("Dendësi maksimale", "Maximum density"),
  spacing_compact: tx("Kompakt", "Compact"),
  spacing_balanced: tx("I balancuar", "Balanced"),
  spacing_spacious: tx("I gjerë", "Spacious"),
  field_columns: tx("Kolona", "Columns"),
  field_rows: tx("Rreshta", "Rows"),
  field_safePadXMM: tx("Margjina X", "Page margin X"),
  field_safePadYMM: tx("Margjina Y", "Page margin Y"),
  field_labelColumnGapMM: tx("Hapësira mes kolonave", "Column gap"),
  field_labelRowGapMM: tx("Hapësira mes rreshtave", "Row gap"),
  decreaseField: tx("Zvogëlo {field}", "Decrease {field}"),
  increaseField: tx("Rrit {field}", "Increase {field}"),
  presetPrice: tx("Çmimi në etiketë", "Price on the label"),
  "pricePolicy_regular-first": tx("Çmimi i rregullt (si deri tani)", "Regular price (as today)"),
  pricePolicy_selling: tx("Çmimi aktual i shitjes", "Current selling price"),
  pricePolicy_hidden: tx("Pa çmim", "No price"),
  presetInvalid: tx("Me këto vlera etiketat nuk nxënë në faqe.", "With these values the labels don't fit on the page."),
  presetOffsetsNote: tx(
    "Zhvendosjet X/Y të printerit ruhen veç e veç në çdo kompjuter, te dritarja e printimit.",
    "Printer X/Y offsets are saved separately on each computer, in the print window."
  ),
  presetSave: tx("Ruaj fletën", "Save sheet"),
  presetSaved: tx("Fleta e etiketave u ruajt.", "Label sheet saved."),
  configurationTitle: tx("Konfigurimi i printimit", "Print configuration"),
  configurationCopy: tx("Përmasat e fletës ruhen për kompaninë; kalibrimi ruhet vetëm në këtë pajisje.", "Sheet dimensions are shared with the company; calibration is saved only on this device."),
  savedPresets: tx("Presetet e ruajtura", "Saved presets"),
  presetName: tx("Emri i presetit", "Preset name"),
  defaultPreset: tx("Kryesor", "Default"),
  duplicatePreset: tx("Dupliko", "Duplicate"),
  makeDefault: tx("Bëje kryesor", "Make default"),
  deletePreset: tx("Fshije", "Delete"),
  presetDuplicated: tx("Preseti u duplikua.", "Preset duplicated."),
  presetDeleted: tx("Preseti u fshi.", "Preset deleted."),
  presetDeleteConfirm: tx("Ta fshijmë këtë preset?", "Delete this preset?"),
  resetDefaults: tx("Rikthe standardin", "Reset to defaults"),
  presetMetaDetailed: tx("{paper} · {columns} × {rows} = {slots} etiketa · {width} × {height} mm", "{paper} · {columns} × {rows} = {slots} labels · {width} × {height} mm"),
  sectionPaperLayout: tx("Fleta dhe etiketat", "Paper & Label Layout"),
  sectionMarginsSpacing: tx("Margjinat dhe hapësirat", "Margins & Spacing"),
  sectionCalibration: tx("Kalibrimi i printerit", "Printer Calibration"),
  sectionTypography: tx("Tipografia dhe përmbajtja", "Typography & Content"),
  sectionBarcode: tx("Cilësimet e barkodit", "Barcode Settings"),
  sectionPreview: tx("Parapamja e printimit", "Print Preview"),
  paperSize: tx("Formati i fletës", "Paper size"), paperCustom: tx("Përmasa tjera", "Custom size"),
  orientation: tx("Orientimi", "Orientation"), portrait: tx("Vertikal", "Portrait"), landscape: tx("Horizontal", "Landscape"),
  paperWidth: tx("Gjerësia e fletës", "Paper width"), paperHeight: tx("Lartësia e fletës", "Paper height"),
  field_labelWidthMM: tx("Gjerësia e etiketës", "Label width"), field_labelHeightMM: tx("Lartësia e etiketës", "Label height"),
  field_marginTopMM: tx("Margjina sipër", "Top margin"), field_marginRightMM: tx("Margjina djathtas", "Right margin"),
  field_marginBottomMM: tx("Margjina poshtë", "Bottom margin"), field_marginLeftMM: tx("Margjina majtas", "Left margin"),
  field_paddingTopMM: tx("Padding sipër", "Top padding"), field_paddingRightMM: tx("Padding djathtas", "Right padding"),
  field_paddingBottomMM: tx("Padding poshtë", "Bottom padding"), field_paddingLeftMM: tx("Padding majtas", "Left padding"),
  style_title: tx("Titulli", "Product title"), style_color: tx("Ngjyra", "Colour"), style_size: tx("Madhësia", "Size"), style_price: tx("Çmimi", "Price"),
  fontSize: tx("Madhësia e shkronjave", "Font size"), fontWeight: tx("Trashësia", "Font weight"), alignment: tx("Rreshtimi", "Alignment"), maxLines: tx("Rreshta maksimalë", "Maximum lines"),
  overflowBehavior: tx("Teksti i gjatë", "Long text"), overflowClip: tx("Preje", "Clip"), overflowEllipsis: tx("Trego …", "Show …"),
  alignLeft: tx("Majtas", "Left"), alignCenter: tx("Në qendër", "Centre"), alignRight: tx("Djathtas", "Right"), showPrice: tx("Shfaq çmimin", "Show price"),
  barcodeWidth: tx("Gjerësia e barkodit", "Barcode width"), barcodeHeight: tx("Lartësia e barkodit", "Barcode height"), barcodePadding: tx("Hapësira anësore", "Side spacing"), barcodeTextSize: tx("Teksti i barkodit", "Barcode text size"), barcodeSpacing: tx("Hapësira e brendshme", "Internal spacing"), showBarcodeText: tx("Shfaq numrin e barkodit", "Show barcode number"),
  barcodeSafetyNote: tx("CODE128 ruhet gjithmonë. Sistemi bllokon barkode tepër të ngjeshura derisa të verifikohen me skaner.", "CODE128 is always preserved. The system blocks dangerously dense barcodes until they are test-scanned."),
  layoutInvalidTitle: tx("Konfigurimi nuk nxë fizikisht", "Configuration does not physically fit"),
  layoutInvalidCopy: tx("Printimi është bllokuar derisa të korrigjohen përmasat ose kalibrimi.", "Printing is blocked until the dimensions or calibration are corrected."),
  layoutIssue_paper_width_invalid: tx("Gjerësia e fletës është e pavlefshme", "Paper width is invalid"), layoutIssue_paper_height_invalid: tx("Lartësia e fletës është e pavlefshme", "Paper height is invalid"),
  layoutIssue_negative_margin: tx("Margjinat s'mund të jenë negative", "Margins cannot be negative"), layoutIssue_negative_padding: tx("Padding-u s'mund të jetë negativ", "Padding cannot be negative"), layoutIssue_negative_gap: tx("Hapësira s'mund të jetë negative", "Gaps cannot be negative"),
  layoutIssue_label_width_not_positive: tx("Gjerësia e etiketës duhet të jetë pozitive", "Label width must be positive"), layoutIssue_label_height_not_positive: tx("Lartësia e etiketës duhet të jetë pozitive", "Label height must be positive"),
  layoutIssue_label_width_too_small: tx("Etiketa është më e ngushtë se 20 mm", "Label is narrower than 20 mm"), layoutIssue_label_height_too_small: tx("Etiketa është më e ulët se 15 mm", "Label is shorter than 15 mm"),
  layoutIssue_horizontal_padding_overflow: tx("Padding-u horizontal mbush etiketën", "Horizontal padding consumes the label"), layoutIssue_vertical_padding_overflow: tx("Padding-u vertikal mbush etiketën", "Vertical padding consumes the label"),
  layoutIssue_barcode_width_not_positive: tx("Padding-u i barkodit nuk lë gjerësi për vijat", "Barcode padding leaves no width for the bars"),
  layoutIssue_content_height_overflow: tx("Përmbajtja dhe barkodi nuk nxënë pa u mbivendosur", "Content and barcode do not fit without overlapping"),
  layoutIssue_grid_width_overflow: tx("Kolonat dalin jashtë fletës", "Columns extend beyond the page"), layoutIssue_grid_height_overflow: tx("Rreshtat dalin jashtë fletës", "Rows extend beyond the page"),
  layoutIssue_offset_left_overflow: tx("Kalibrimi X del majtas fletës", "X calibration extends left of the page"), layoutIssue_offset_right_overflow: tx("Kalibrimi X pret kolonën e fundit", "X calibration clips the last column"),
  layoutIssue_offset_top_overflow: tx("Kalibrimi Y del sipër fletës", "Y calibration extends above the page"), layoutIssue_offset_bottom_overflow: tx("Kalibrimi Y pret rreshtin e fundit", "Y calibration clips the last row"),
  previewZoom: tx("Zmadhimi i parapamjes", "Preview zoom"), previewZoomOut: tx("Zvogëlo parapamjen", "Zoom preview out"), previewZoomIn: tx("Zmadho parapamjen", "Zoom preview in"), previewFit: tx("Përshtate", "Fit"),
  legacyFoundTitle: tx("U gjetën cilësimet e vjetra të këtij shfletuesi", "This browser has older label settings"),
  legacyFoundCopy: tx(
    "Paneli i vjetër i printimit këtu përdor {columns} × {rows}. Mund t'i përdorësh si fletë të kompanisë.",
    "The old print panel here uses {columns} × {rows}. You can use them as the company sheet."
  ),
  legacyUse: tx("Përdori këto", "Use these"),

  // ── "Print X labels" links (restock, receiving, new product) ─────────────
  historyPrints: tx("Printimet", "Prints"),
  historyDismissals: tx("Të shënuara si të panevojshme", "Marked not needed"),
  dismissalsEmptyTitle: tx("Asnjë etiketë e shënuar si e panevojshme", "No labels marked as not needed"),
  dismissalsEmptyCopy: tx(
    "Kur dikush shënon etiketa si të panevojshme, sasia, arsyeja, personi dhe koha ruhen këtu.",
    "When someone marks labels as not needed, the quantity, reason, person and time are kept here."
  ),
  dismissalClosed: tx("Mbyllur", "Closed"),
  historyNoMatchTitle: tx("Asnjë printim me këto filtra", "No prints match these filters"),
  needsAllPendingTitle: tx(
    "Gjithçka e pranuar është në printime që presin konfirmim",
    "Everything received is in prints awaiting confirmation"
  ),
  receiptLineCounts: tx("{received} të pranuara · {open} pa etiketë", "{received} received · {open} still need labels"),
  entryReceivedCount: tx("{count} të pranuara këtë ditë", "{count} received this day"),
  heldInJob: tx("{count} etiketa janë tani në punën #{job} të {name}.", "{count} labels are currently in {name}'s print job #{job}."),
  heldInJobOne: tx("1 etiketë është tani në punën #{job} të {name}.", "1 label is currently in {name}'s print job #{job}."),
  dismissedCountOne: tx("{count} e shënuar si e panevojshme", "{count} marked not needed"),
  dismissedCountOther: tx("{count} të shënuara si të panevojshme", "{count} marked not needed"),
  dismissalLine: tx("{count} · “{reason}” · {name} · {time}", "{count} · “{reason}” · {name} · {time}"),
  selectByReceipt: tx("Zgjidh sipas pranimit", "Select by receipt"),
  selectReceipt: tx("{receipt} · {count}", "{receipt} · {count}"),
  receiptName_restock: tx("Rimbushja #{id}", "Restock #{id}"),
  receiptName_scan_receive: tx("Skanimi #{id}", "Receiving scan #{id}"),
  receiptName_initial_stock: tx("Stoku fillestar #{id}", "Opening stock #{id}"),
  receiptsName: tx("{count} pranime", "{count} receipts"),
  receiptLoading: tx("Po hapen etiketat e mallit të pranuar…", "Opening the labels for the received stock…"),
  receiptAddedOne: tx("1 etiketë u shtua nga {receipt}.", "1 label added from {receipt}."),
  receiptAddedOther: tx("{count} etiketa u shtuan nga {receipt}.", "{count} labels added from {receipt}."),
  receiptPartial: tx(
    "{count} nga {required} etiketat nga {receipt} kanë ende nevojë për printim.",
    "{count} of the original {required} labels from {receipt} still need printing."
  ),
  receiptNothingLeft: tx("Nuk ka mbetur asgjë për printim nga {receipt}.", "Nothing left to print from {receipt}."),
  receiptNotNeeded: tx(
    "{receipt} u shënua si e etiketuar; nuk ka etiketa që duhen. Mund të printosh nga katalogu nëse të duhen.",
    "{receipt} was marked as already labelled; no labels are needed. Print from the catalog if you need any."
  ),
  receiptAlreadyInTray: tx(
    "Etiketat nga {receipt} janë tashmë në listën e printimit; nuk u shtua asgjë dy herë.",
    "The labels from {receipt} are already in your print list; nothing was added twice."
  ),
  receiptReasonPrinted: tx("{count} të printuara", "{count} already printed"),
  receiptReasonDismissed: tx("{count} të shënuara si të panevojshme", "{count} marked not needed"),
  receiptReasonPending: tx("{count} në punën #{job} nga {name} që pret konfirmim", "{count} in {name}'s job #{job} awaiting confirmation"),
  receiptMerged: tx(
    "U shtuan te {count} etiketat që ishin tashmë në listë.",
    "Added to the {count} labels already in your list."
  ),
  receiptNotFoundOne: tx("Pranimi #{ids} nuk u gjet.", "Receipt #{ids} was not found."),
  receiptNotFoundOther: tx("Pranimet #{ids} nuk u gjetën.", "Receipts #{ids} were not found."),
  receiptLoadError: tx(
    "Etiketat e mallit të pranuar nuk u hapën. Gjej ato te “Pa etiketa”.",
    "Could not open the labels for the received stock. Find them under “Needs labels”."
  ),

  // ── Unconfirmed prints (shown on every visit) ────────────────────────────
  recoveryTitleMine: tx("Ke një printim që pret konfirmim", "You have a print awaiting confirmation"),
  recoveryTitleMineOther: tx("Ke {count} printime që presin konfirmim", "You have {count} prints awaiting confirmation"),
  recoveryTitleOthers: tx("{count} printime të të tjerëve presin konfirmim", "{count} prints by others are awaiting confirmation"),
  recoveryTitleOthersOne: tx("1 printim i dikujt tjetër pret konfirmim", "1 print by someone else is awaiting confirmation"),
  recoveryJob: tx("Puna #{job} · {labels} · {time}", "Job #{job} · {labels} · {time}"),
  recoveryJobBy: tx("Puna #{job} · {labels} · {name} · {time}", "Job #{job} · {labels} · {name} · {time}"),
  newLabelCountOne: tx("{count} etiketë e re", "{count} new-stock label"),
  newLabelCountOther: tx("{count} etiketa të reja", "{count} new-stock labels"),
  presetLoadErrorTitle: tx("Fleta e etiketave nuk u ngarkua", "The label sheet settings did not load"),
  presetLoadErrorCopy: tx(
    "Pa to nuk mund të shfaqet pamja paraprake as të printohet. Zgjedhja jote ruhet; provo përsëri.",
    "Without them nothing can be previewed or printed. Your selection is kept; try again."
  ),
  cellOnHand: tx("{count} në stok", "{count} on hand"),
  issueArchived: tx("Produkti është i arkivuar — kontrollo nëse etiketa duhet ende.", "This product is archived — check the label is still needed."),
  deviceTitle: tx("Ky kompjuter", "This computer"),
  deviceOffsets: tx("Zhvendosja e printerit: X {x} mm · Y {y} mm", "Printer offset: X {x} mm · Y {y} mm"),
  deviceSeededFromPanel: tx("marrë nga paneli i vjetër i etiketave", "taken from the old label panel"),
  recoveryOpen: tx("Hap", "Open"),
  openJobNumber: tx("Hap punën #{job}", "Open job #{job}"),
  recoveryConfirm: tx("Konfirmo", "Confirm"),
  relativeJustNow: tx("tani", "just now"),
  relativeMinutes: tx("{count} min më parë", "{count} min ago"),
  relativeHours: tx("{count} orë më parë", "{count} h ago"),
  relativeDays: tx("{count} ditë më parë", "{count} d ago"),
};

export default printCenterTranslations;
